import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { cn } from "@/shared/utils/core";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { formatContactName } from "@/shared/utils/contactUtils";
import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";

import { parseConsentEntries } from "@/shared/utils/consentUtils";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { ConsentEntry, HeirConsentOption } from "@/shared/types/probateTypes";

const CONSENT_ANSWER_KEY = "family_signoff_consents";

interface ConsentOption {
  value: HeirConsentOption;
  label: string;
}

function buildConsentOptions(): ConsentOption[] {
  return [
    { value: "willing_to_sign", label: "Yes, they are willing to sign" },
    { value: "uncooperative", label: "No, they refuse or won't cooperate" },
    { value: "minor_or_incapacitated", label: "They are under 18 or unable to make legal decisions" },
    { value: "cannot_locate", label: "I can't find or locate them" },
    { value: "deceased_after", label: "They have passed away" },
  ];
}

const CONSENT_OPTIONS = buildConsentOptions();

export const SignoffLoopScreen: FC<FlowCustomComponentProps> = ({ answers, setAnswer, onComplete }) => {
  const { person } = useAppContext();
  const { contacts, isLoading } = useUserContacts();
  const decedentFirstName = person?.first_name ?? "the decedent";

  const signoffContacts = useMemo(
    () => contacts.filter((c) => c.sign_off_status === "needed"),
    [contacts],
  );

  const savedConsents = useMemo(() => parseConsentEntries(answers[CONSENT_ANSWER_KEY]), [answers]);

  const initialIndex = useMemo(() => {
    const first = signoffContacts.findIndex((c) => !savedConsents[String(c.id)]);
    return first >= 0 ? first : 0;
  }, [signoffContacts, savedConsents]);

  const externalIndex = Number(answers["signoff_loop_index"] ?? "-1");
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (externalIndex >= 0 && externalIndex !== currentIndex) {
      setCurrentIndex(externalIndex);
    }
  }, [externalIndex, currentIndex]);

  const currentContact = signoffContacts[currentIndex] ?? null;
  const total = signoffContacts.length;
  const currentEntry = currentContact ? savedConsents[String(currentContact.id)] : null;
  const currentSelection = currentEntry?.status ?? "";

  const setAnswerRef = useRef(setAnswer);
  setAnswerRef.current = setAnswer;

  const savedConsentsRef = useRef(savedConsents);
  savedConsentsRef.current = savedConsents;

  useEffect(() => {
    setAnswerRef.current("signoff_loop_index", String(currentIndex));
    setAnswerRef.current("signoff_loop_total", String(total));
    const contact = signoffContacts[currentIndex];
    const hasSelection = contact ? !!savedConsentsRef.current[String(contact.id)] : false;
    setAnswerRef.current("signoff_loop_has_selection", hasSelection ? "true" : "");
  }, [currentIndex, total, signoffContacts]);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (total === 0 && !isLoading) {
      enqueueSnackbar("No sign-offs needed for this estate.", { variant: "info" });
      onCompleteRef.current();
    }
  }, [total, isLoading]);

  const handleSelect = (value: string) => {
    if (!currentContact) return;
    const name = formatContactName(currentContact.first_name, currentContact.last_name);
    const entry: ConsentEntry = { name, status: value as HeirConsentOption };
    const updated = { ...savedConsents, [String(currentContact.id)]: entry };
    setAnswer(CONSENT_ANSWER_KEY, JSON.stringify(updated));
    setAnswer("signoff_loop_has_selection", "true");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-brand" />
      </div>
    );
  }

  if (total === 0) {
    return null;
  }

  const name = currentContact
    ? formatContactName(currentContact.first_name, currentContact.last_name)
    : "";
  const relationshipLabel = currentContact?.relationship
    ? getRelationshipLabel(currentContact.relationship)
    : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Will {name} sign a court consent form?
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          As {decedentFirstName}'s {relationshipLabel ? relationshipLabel.toLowerCase() : "family member"},{" "}
          {name} needs to be notified or give permission for probate to move forward.
        </p>
      </div>

      <RadioGroup
        name="signoff_consent"
        value={currentSelection}
        onValueChange={handleSelect}
        className="flex flex-col gap-3"
      >
        {CONSENT_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={cn(
              "flex items-center gap-2 rounded-lg bg-white p-4 cursor-pointer border-solid",
              currentSelection === option.value
                ? "border-[1.5px] border-focus"
                : "border border-primary",
            )}
            onClick={() => handleSelect(option.value)}
          >
            <RadioGroupItem value={option.value} />
            <span className="text-sm font-medium">{option.label}</span>
          </label>
        ))}
      </RadioGroup>
    </div>
  );
};

export function getSignoffLoopState(answers: Record<string, string | string[]>): {
  index: number;
  total: number;
  hasSelection: boolean;
} {
  return {
    index: Math.max(0, Number(answers["signoff_loop_index"] ?? "0")),
    total: Number(answers["signoff_loop_total"] ?? "0"),
    hasSelection: answers["signoff_loop_has_selection"] === "true",
  };
}
